import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/state/auth';
import { useAuthStatus } from '@/lib/query';
import { ChangePasswordModal } from '@/components/ChangePasswordModal';

/**
 * Route guard. In demo mode the backend auto-authenticates, so we hydrate a
 * local operator identity and render straight through. In jwt mode an
 * unauthenticated visitor is redirected to /login.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const { data: status, isLoading } = useAuthStatus();
  const { user, setUser, setMode } = useAuth();
  const loc = useLocation();

  useEffect(() => {
    if (!status) return;
    setMode(status.mode);
    if (status.mode === 'demo' && status.user) {
      setUser(status.user, null);
    }
  }, [status, setMode, setUser]);

  if (isLoading) {
    return (
      <div className="grid h-screen place-items-center text-slate-400">正在初始化监控台…</div>
    );
  }

  if (status?.mode === 'jwt' && !user) {
    return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  }

  // First-login (or admin-reset) accounts must change their password before using the app.
  if (user?.mustChangePassword) {
    return <ChangePasswordModal forced />;
  }

  return <>{children}</>;
}
